import React from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {Button, EMPHASIS, Text} from '@forecast-it/design-system';

/*
 * The "changed from the company default" treatment of the export windows (T4-151, spec 028), shared with
 * the Xero window (T4-153): an amber dot after a changed option's label, a line stating the default, and
 * one reset for the whole group.
 */

/*
 * The design system has no dot or badge, and its Pill has no warning variant. 8px matches the dot inside
 * Pill. inline-block, or a span ignores its width and height.
 *
 * The negative right margin (the dot's width plus its left margin, 12px) makes it take no room on the line,
 * so the browser never breaks before it and it cannot wrap alone onto its own line after a word that just
 * fits. It hangs at most 12px past the word, inside the switch grid's 20px column gap; a caller must leave
 * that much room after it.
 */
const Dot = styled.span`
	display: inline-block;
	width: ${props => props.theme.sys.spacing['200']};
	height: ${props => props.theme.sys.spacing['200']};
	margin-left: ${props => props.theme.sys.spacing['100']};
	margin-right: calc(-1 * (${props => props.theme.sys.spacing['200']} + ${props => props.theme.sys.spacing['100']}));
	border-radius: 50%;
	background-color: ${props => props.theme.sys.color.iconfill.warning};
	vertical-align: middle;
`;

/*
 * Always rendered and only hidden while the option matches its default, so the label wraps the same way
 * in both states and the options never move (spec 028, D7). Decorative: the line below says the same in
 * words, so the change is never shown by colour alone.
 */
export const ChangedFromDefaultDot = ({visible}: {visible: boolean}): JSX.Element => (
	<Dot aria-hidden={true} data-cy={'export-default-changed-dot'} style={{visibility: visible ? 'visible' : 'hidden'}} />
);

/* "Company default: On/Off". The id lets the option's group name it as its description. */
export const CompanyDefaultHint = ({id, defaultValue}: {id: string; defaultValue: boolean}): JSX.Element => {
	const {formatMessage} = useIntl();
	return (
		<div id={id}>
			<Text size={'2'} color={'auxiliary'}>
				{formatMessage({id: 'invoicing.export_defaults.company_default'}, {value: defaultValue ? 'on' : 'off'})}
			</Text>
		</div>
	);
};

/*
 * A button, not a link: it acts and goes nowhere. onPress, because the design-system Button drops onClick;
 * a plain-string child, because the Button builds its aria-label from children.toString(). The pointer type
 * lets the window move focus only after a keyboard or screen-reader press (spec 028, D14).
 */
export const ResetToCompanyDefaultsButton = ({
	onReset,
	disabled,
}: {
	onReset: (pointerType: string) => void;
	disabled: boolean;
}): JSX.Element => {
	const {formatMessage} = useIntl();
	return (
		<Button
			emphasis={EMPHASIS.LOW}
			icon={'reload'}
			onPress={event => onReset(event.pointerType)}
			disabled={disabled}
			data-cy={'reset-to-company-defaults'}
		>
			{formatMessage({id: 'invoicing.export_defaults.reset_to_company_defaults'})}
		</Button>
	);
};
